import React, { useEffect, useRef, useState } from "react";
import { Building2, Check, ChevronDown } from "lucide-react";
import { useOrg } from "@/hooks/useOrg";

export function OrgSwitcher() {
  const { orgs, currentOrg, setCurrentOrg } = useOrg();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (orgs.length === 0) return null;

  // Single org: render a static label (no menu).
  if (orgs.length === 1) {
    return (
      <div className="flex items-center gap-xs px-sm py-xs rounded-md text-sm text-text/80">
        <Building2 size={14} className="text-textLight" />
        <span className="font-medium truncate max-w-[180px]">{orgs[0].name}</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-xs px-sm py-xs rounded-md border border-border bg-surface text-sm text-text hover:border-brand/40 hover:bg-brand/5 transition-colors cursor-pointer"
      >
        <Building2 size={14} className="text-brand" />
        <span className="font-medium truncate max-w-[180px]">
          {currentOrg?.name ?? "Select organization"}
        </span>
        <ChevronDown size={14} className="text-textLight" />
      </button>
      {open && (
        <div className="absolute left-0 mt-xs w-72 rounded-md border border-border bg-surface shadow-card overflow-hidden z-30">
          <div className="px-md py-xs text-[10px] uppercase tracking-wider text-textLight border-b border-border">
            Organizations
          </div>
          <ul className="max-h-72 overflow-y-auto py-xs">
            {orgs.map((o) => {
              const isCurrent = o.id === currentOrg?.id;
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => { setCurrentOrg(o.id); setOpen(false); }}
                    className={`w-full text-left flex items-start gap-sm px-md py-sm hover:bg-bg ${
                      isCurrent ? "bg-brand/5" : ""
                    }`}
                  >
                    <Building2 size={14} className={`mt-[3px] ${isCurrent ? "text-brand" : "text-textLight"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text truncate">{o.name}</div>
                      <div className="text-xs text-textLight truncate">
                        {o.slug ?? "—"}
                      </div>
                    </div>
                    {isCurrent && <Check size={14} className="text-brand mt-[3px]" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
